/**
 * GrainOverlay - Subtle texture overlay for premium feel
 *
 * Uses CSS background-image with inline SVG for performance
 * (avoiding SVG filter which can cause scroll jank on mobile)
 */
export function GrainOverlay({
  opacity = 0.04,
  className = '',
}: {
  opacity?: number;
  className?: string;
}) {
  return (
    <div
      className={`pointer-events-none absolute inset-0 z-[1] ${className}`}
      style={{
        opacity,
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
      }}
      aria-hidden="true"
    />
  );
}

/**
 * FloatingBlob - Animated background element for visual depth
 */
export function FloatingBlob({
  className = '',
  color = 'primary',
  size = 'lg',
  position = 'top-left',
  delay = 0,
}: {
  className?: string;
  color?: 'primary' | 'accent';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center';
  delay?: number;
}) {
  const sizeClasses = {
    sm: 'size-[200px]',
    md: 'size-[300px]',
    lg: 'size-[400px]',
    xl: 'size-[500px]',
  };

  const positionClasses = {
    'top-left': 'top-0 left-0',
    'top-right': 'top-0 right-0',
    'bottom-left': 'bottom-0 left-0',
    'bottom-right': 'bottom-0 right-0',
    center: 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
  };

  const colorClasses = {
    primary: 'bg-primary/10',
    accent: 'bg-accent/8',
  };

  return (
    <div
      className={`
        absolute rounded-full blur-[100px]
        ${sizeClasses[size]}
        ${positionClasses[position]}
        ${colorClasses[color]}
        ${className}
      `}
      style={{
        animation: `float-blob 8s ease-in-out infinite`,
        animationDelay: `${delay}ms`,
      }}
      aria-hidden="true"
    />
  );
}
